import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { references as mockReferences } from '../data/mock';
import Layout from '../components/layout/Layout';
import { getText, staticTexts } from '../utils/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReferencesPage = () => {
  const { t, language } = useLanguage();
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);

  const txt = staticTexts.references;

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/references`)
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setReferences(data);
        } else {
          setReferences(mockReferences);
        }
      })
      .catch(() => {
        setReferences(mockReferences);
      })
      .finally(() => setLoading(false));
  }, []);

  // Helper to get reference title
  const getReferenceTitle = (ref) => {
    if (language === 'ar' && ref.title_ar) return ref.title_ar;
    if (language === 'fr' && ref.title_fr) return ref.title_fr;
    return ref.title_en || ref.title_fr || '';
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
            {getText(language, txt.trustedClients.en, txt.trustedClients.fr, txt.trustedClients.ar)}
          </p>
          <h1 className="text-5xl md:text-6xl font-light text-slate-800">
            {getText(language, txt.title.en, txt.title.fr, txt.title.ar)}
          </h1>
        </div>
      </section>

      {/* References Grid */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {references.map((ref) => (
              <div
                key={ref.id}
                className="bg-white rounded-xl p-8 flex flex-col items-center justify-center hover:shadow-md transition-shadow duration-300"
              >
                <img
                  src={ref.image}
                  alt={getReferenceTitle(ref) || `Reference ${ref.id}`}
                  className="max-h-20 w-auto"
                />
                {getReferenceTitle(ref) && (
                  <p className="text-sm text-slate-500 mt-3 text-center">
                    {getReferenceTitle(ref)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 bg-slate-100">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-light text-slate-800 mb-6">
              {getText(language, 
                'Trusted by',
                'La Confiance des',
                'موثوق من قبل'
              )}<br />
              <span className="font-semibold text-slate-900">
                {getText(language,
                  'Industry Leaders',
                  'Leaders de l\'Industrie',
                  'رواد الصناعة'
                )}
              </span>
            </h2>
            <p className="text-slate-500">
              {getText(language,
                'For over 25 years, we have built strong relationships with major industrial and agricultural companies across Algeria.',
                'Depuis plus de 25 ans, nous avons construit des relations solides avec les grandes entreprises industrielles et agricoles à travers l\'Algérie.',
                'على مدار أكثر من 25 عامًا، بنينا علاقات قوية مع الشركات الصناعية والزراعية الكبرى في جميع أنحاء الجزائر.'
              )}
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ReferencesPage;
